import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { ImageMetadata } from '../../services/imageService';

interface ImageLibraryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  images: ImageMetadata[];
  videos: ImageMetadata[];
  title?: string;
  usedImageUrls?: Set<string>;
  /** ライブラリ取得中の場合 true。取得中は「画像がありません」の代わりに「取得中」を表示 */
  loading?: boolean;
}

// ファイルサイズをフォーマット
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const ImageLibraryModal: React.FC<ImageLibraryModalProps> = ({
  open,
  onClose,
  onSelect,
  images,
  videos,
  title = 'ライブラリから選択',
  usedImageUrls = new Set(),
  loading = false,
}) => {
  const [activeTab, setActiveTab] = useState(0); // 0: 画像, 1: 動画
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValue, setFilterValue] = useState('all');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleImageConfirm = (imageUrl: string) => {
    onSelect(imageUrl);
    onClose();
  };

  // フィルタリング
  const filteredImages = useMemo(() => {
    return images.filter(img => {
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        if (!img.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      if (filterValue !== 'all') {
        const isUsed = usedImageUrls.has(img.url);
        if (filterValue === 'used' && !isUsed) return false;
        if (filterValue === 'unused' && isUsed) return false;
      }

      return true;
    });
  }, [images, searchQuery, filterValue, usedImageUrls]);

  const filteredVideos = useMemo(() => {
    return videos.filter(vid => {
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        if (!vid.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      if (filterValue !== 'all') {
        const isUsed = usedImageUrls.has(vid.url);
        if (filterValue === 'used' && !isUsed) return false;
        if (filterValue === 'unused' && isUsed) return false;
      }

      return true;
    });
  }, [videos, searchQuery, filterValue, usedImageUrls]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '42px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal */}
        <Box
          sx={{
            width: '1000px',
            height: '640px',
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <TextField
            placeholder="ファイル名で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: '500px',
              minWidth: '500px',
              maxWidth: '500px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                backgroundColor: '#E7E7E7',
                height: '60px',
                padding: '0 12px',
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  color: '#000000',
                  fontSize: '16px',
                  padding: 0,
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#666666',
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1.5 }}>
                  <SearchIcon sx={{ fontSize: '22px', color: '#666666' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchQuery('')}
                    size="small"
                    sx={{
                      color: '#666666',
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            sx={{
              minWidth: 100,
              borderRadius: '16px',
              backgroundColor: '#E7E7E7',
              height: '60px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select': {
                color: '#000000',
                fontSize: '16px',
                padding: '0 12px',
              },
            }}
          >
            <MenuItem value="all">すべて</MenuItem>
            <MenuItem value="used">使用中</MenuItem>
            <MenuItem value="unused">未使用</MenuItem>
          </Select>
        </Box>

        {/* Tabs */}
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              px: 3,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: '#1B76D2',
              },
            }}
          >
            <Tab
              label={`画像 (${filteredImages.length})`}
            />
            <Tab
              label={`動画 (${filteredVideos.length})`}
            />
          </Tabs>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
          }}
        >
          {activeTab === 0 ? (
            // 画像タブ
            loading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <LinearProgress sx={{ maxWidth: 320, mx: 'auto', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  取得中...
                </Typography>
              </Box>
            ) : filteredImages.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? '検索結果がありません' : '画像がありません'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredImages.map((image) => (
                  <Grid size={{ xs: 4 }} key={image.url}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleImageConfirm(image.url)}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '75%', // 4:3 アスペクト比
                          backgroundColor: '#F5F5F5',
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={image.url}
                          alt={image.name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {image.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '11px',
                            color: '#999999',
                          }}
                        >
                          {formatFileSize(image.size)}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            // 動画タブ
            loading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <LinearProgress sx={{ maxWidth: 320, mx: 'auto', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  取得中...
                </Typography>
              </Box>
            ) : filteredVideos.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? '検索結果がありません' : '動画がありません'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredVideos.map((video) => (
                  <Grid size={{ xs: 4 }} key={video.url}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleImageConfirm(video.url)}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '75%',
                          backgroundColor: '#F5F5F5',
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={video.url}
                          alt={video.name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {video.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '11px',
                            color: '#999999',
                          }}
                        >
                          {formatFileSize(video.size)}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          )}
        </Box>
      </Box>

      {/* Close Button (モーダルの外) */}
      <Button
        onClick={onClose}
        sx={{
          backgroundColor: '#666666',
          color: '#FFFFFF',
          borderRadius: '8px',
          textTransform: 'none',
          px: 4,
          py: 1.5,
          '&:hover': {
            backgroundColor: '#555555',
          },
        }}
      >
        閉じる
      </Button>
    </Box>
    </Box>
  );
};

